/**
 * @fileoverview Device and request identity helpers.
 * @description Generates the identifiers the app sends to the backend: a device ID (one per
 * browser/device, survives restarts via localStorage — the practical substitute for a hardware MAC
 * address or OS computer name, neither of which any browser exposes to JavaScript), sent in the
 * login, MFA and password-reset request bodies; and a correlation ID (fresh per call, identifies
 * that single request in logs/tracing, per the backend's identity API contract). These are plain
 * identifiers, not secrets, so localStorage is an intentional choice here — unlike auth tokens,
 * there's nothing to protect by hiding them from JS.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { deviceConfig } from '@/config/device-config'

/**
 * @description Reads the persisted device ID, generating and persisting a new one on first use.
 * @returns {string} The device ID, stable across sessions for this browser/device.
 */
export function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(deviceConfig.deviceIdStorageKey)

  if (existing) {
    return existing
  }

  const deviceId = crypto.randomUUID()
  localStorage.setItem(deviceConfig.deviceIdStorageKey, deviceId)
  return deviceId
}

/**
 * @description Generates a fresh correlation identifier for a single outgoing request.
 * @returns {string} A new correlation ID, unique to this one call.
 */
export function generateCorrelationId(): string {
  return crypto.randomUUID()
}
